import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Anchor, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { post, errMsg } from "@/lib/api";
import { DemoBanner } from "@/components/Layout";
import { Loading } from "@/components/common";

export function LoginPage() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const error = location.state?.error;
  if (loading) return <Loading label="Checking session…" />;
  if (user) return <Navigate to="/dashboard" replace />;

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#0A1612] text-slate-200">
      <DemoBanner />
      <div className="grid flex-1 lg:grid-cols-[1.1fr_1fr]">
        <section className="relative hidden overflow-hidden border-r border-[#1E3E30] bg-[#11241C] p-12 lg:flex lg:flex-col lg:justify-between">
          <div className="grid-bg absolute inset-0 opacity-40" />
          <div className="relative flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-lime-500/15 text-lime-300"><Anchor className="h-6 w-6" /></div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight">NIMACEMS</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Nigerian Maritime Onshore Continuous Emission Monitoring</div>
            </div>
          </div>
          <div className="relative max-w-xl">
            <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-slate-100 sm:text-5xl">Detect episodes. Rank possible sources. <span className="text-amber-300">Say “inconclusive” when it is.</span></h1>
            <p className="mt-6 text-base text-slate-400">Fuses onshore air-quality readings, wind, AIS vessel positions and port activity into an explainable, provisional ranking — with human review as the only route to a verified case.</p>
            <ul className="mt-8 grid gap-2 font-mono text-xs text-slate-500">
              <li>· Rank + evidence grade, never a “percentage of guilt”</li>
              <li>· Candidates, never polluters — no automatic notices</li>
              <li>· Immutable, versioned attribution runs</li>
              <li>· UTC first, Africa/Lagos alongside</li>
            </ul>
          </div>
          <div className="relative font-mono text-[11px] text-slate-600">Tin Can Island pilot · UPS / NIMASA · demonstrator build on labelled synthetic data</div>
        </section>
        <section className="flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <h2 className="font-display text-lg font-semibold text-slate-100">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">Access is limited to staff on the port administrator's allow-list. The first sign-in becomes the administrator.</p>
            {error && <div data-testid="login-error" className="mt-4 flex gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />{error}</div>}
            <button data-testid="google-signin-button" onClick={signIn} className="mt-6 flex h-11 w-full items-center justify-center gap-3 rounded bg-slate-100 text-sm font-semibold text-slate-900 transition-colors hover:bg-white">
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.5 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6c4.5-4.2 6.9-10.3 6.9-17.7z"/><path fill="#FBBC05" d="M10.5 28.6A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.1.8-4.6l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.7-2.1 15.6-5.7l-7.7-6c-2.1 1.4-4.8 2.3-7.9 2.3-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
              Continue with Google
            </button>
            <p className="mt-6 font-mono text-[11px] text-slate-600">Session cookie is httpOnly, 7-day expiry. Roles: Admin · Staff.</p>
          </div>
        </section>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("session_id");
    (async () => {
      try {
        const data = await post("/auth/session", { session_id: sessionId });
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true, state: { user: data.user } });
      } catch (e) {
        window.history.replaceState(null, "", "/dashboard");
        navigate("/dashboard", { replace: true });
        setError(errMsg(e));
      }
    })();
  }, [navigate, setUser]);

  return <div className="flex min-h-screen items-center justify-center bg-[#0A1612]"><Loading label={error || "Completing sign-in…"} /></div>;
}

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#0A1612]"><Loading label="Loading…" /></div>;
  if (!user) return <div className="flex min-h-screen items-center justify-center bg-[#0A1612]"><Loading label="Backend unavailable — retrying…" /></div>;
  return children;
}

export function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== "ADMIN") return <Navigate to="/dashboard" replace />;
  return children;
}
